import React, { Component, ReactNode } from 'react';
import NewVersionNotice from './NewVersionNotice';
import { isChunkLoadError, markNewVersionReady } from '../utils/newVersion';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error) => void;
  /**
   * What stands in for a crash. 'viewer' (default): the viewer's own error box. 'page': an
   * on-demand page — "This page couldn't open - Reload" instead of a blank app. 'silent': a small
   * window — nothing in its place (the bar under the header still says when a new version is ready).
   * Whatever the kind, a part that could not load because a new version was deployed shows
   * "A new version is ready - Reload" (utils/newVersion.ts).
   */
  kind?: 'viewer' | 'page' | 'silent';
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

class ViewerErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error for debugging
    console.error('WebIFC Viewer Error Boundary caught an error:', error, errorInfo);
    this.setState({ error, errorInfo });
    // A file of the app that is gone: a newer version was deployed after this tab opened.
    if (isChunkLoadError(error)) markNewVersionReady();
    this.props.onError?.(error);
  }

  render() {
    const kind = this.props.kind || 'viewer';
    if (this.state.hasError && isChunkLoadError(this.state.error)) {
      return kind === 'silent' ? null : <NewVersionNotice layout="panel" />;
    }
    if (this.state.hasError && kind === 'silent') return null;
    if (this.state.hasError && kind === 'page') {
      return this.props.fallback || <NewVersionNotice layout="panel" reason="page-error" />;
    }
    if (this.state.hasError) {
      // Custom fallback UI
      return this.props.fallback || (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8f9fa',
          border: '2px dashed #dc3545',
          borderRadius: '8px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '20px',
            color: '#dc3545'
          }}>
            🚨
          </div>

          <h3 style={{
            color: '#dc3545',
            marginBottom: '15px',
            fontSize: '18px',
            fontWeight: 'bold'
          }}>
            WebIFC Viewer Error
          </h3>

          <div style={{
            color: '#6c757d',
            marginBottom: '20px',
            fontSize: '14px',
            maxWidth: '500px',
            lineHeight: '1.5'
          }}>
            The 3D viewer encountered an error and cannot be displayed. This might be due to:
            <ul style={{ textAlign: 'left', marginTop: '10px' }}>
              <li>Browser compatibility issues</li>
              <li>WebGL initialization problems</li>
              <li>Invalid or corrupted IFC files</li>
              <li>Network connectivity issues</li>
            </ul>
          </div>

          {this.state.error && (
            <details style={{
              marginTop: '20px',
              padding: '15px',
              background: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#495057',
              maxWidth: '600px',
              textAlign: 'left'
            }}>
              <summary style={{
                cursor: 'pointer',
                fontWeight: 'bold',
                marginBottom: '10px'
              }}>
                Error Details (click to expand)
              </summary>

              <div style={{ marginBottom: '10px' }}>
                <strong>Error:</strong> {this.state.error.message}
              </div>

              {this.state.error.stack && (
                <div>
                  <strong>Stack Trace:</strong>
                  <pre style={{
                    background: '#f1f3f4',
                    padding: '10px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    overflow: 'auto',
                    maxHeight: '200px'
                  }}>
                    {this.state.error.stack}
                  </pre>
                </div>
              )}
            </details>
          )}

          <button
            onClick={() => {
              this.setState({ hasError: false, error: undefined, errorInfo: undefined });
              window.location.reload();
            }}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            🔄 Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ViewerErrorBoundary;